// 键程总表（读路径，带 family 判别）
export type KeyTravelList = Array<KeyTravelInfo>

/** 写入路径：GCOME 允许 Partial 补丁（feature 内读-改-写合并）。 */
export type KeyTravelWriteList = ReadonlyArray<KeyTravelWriteItem>

/*----------- CH585M / OHID 单键程及死区格式表 -----------*/
export interface ClassicKeyTravel {
  absoluteTrigger: number // 绝对触发位置 10~330 表示 0.10~ 3.30mm
  rtTriggerSensitivity: number // RT 快速触发灵敏度 10~330 表示 0.10~ 3.30mm
  rtResetSensitivity: number // RT 快速触发重置灵敏度 10~330 表示 0.10~ 3.30mm
  rtTriggerEnable: boolean // RT 快速触发开关
  absoluteTriggerEnable: boolean // 绝对触发 开关
  bottomDeadZone: number // 单键底部死区 0~69 表示 0.00~0.69mm
}

/*----------- GCOME_AP 触发行程参数（0xa0/0xa1，每键 8 字节） -----------*/

/** 字节1 低 4bit：0 普通 / 1 RT 关连续 / 2 RT 开连续。 */
export type GcomeTriggerType = 0 | 1 | 2

/** 字节1 高 4bit：0x00 后覆盖 / 0x10 自身优先 / 0x20 绑定优先 / 0x30 同时不生效。 */
export type GcomeSocdStrategy = 0x00 | 0x10 | 0x20 | 0x30

/**
 * GCOME_AP 触发行程。字段名与协议/测试页对齐，不含 CH585M 派生开关。
 * RT 是否开启由 `triggerType !== 0` 得出，不要再写 rtTriggerEnable。
 *
 * 字节布局：字节0 低5bit 轴体类型 + 高3bit 校验(0x5)；字节1 低4bit 触发类型 + 高4bit SOCD 策略；
 * 字节2-3 触发行程(0-399, 0.01mm)；字节3 bit1-4 精度切换；字节4-5/6-7 按下/释放参数
 * （16 位打包：低 9 位 RT 灵敏度 + 高 7 位死区）。
 */
export interface GcomeKeyTravel {
  axisType: number // 轴体类型（字节0 低5bit，编码规则见 types/shaftType.ts）：0=磁玉轴PRO
  triggerType: GcomeTriggerType // 触发类型（字节1 低4bit）：0 普通 / 1 RT 关连续 / 2 RT 开连续
  socdStrategy: GcomeSocdStrategy // SOCD 优先级策略（字节1 高4bit）：0x00/0x10/0x20/0x30
  travel: number // 触发行程 0-399 表示 0.00~3.99mm（字节2-3，0.01mm）
  precisionBits: number // 精度切换（字节3 bit1-4）：按下 bit1-2 / 释放 bit3-4 的 0.01/0.005/0.001 精度位
  pressRt: number // 按下 RT 灵敏度（字节4-5 低9位，0-511；精度由 precisionBits 决定）
  pressDeadzone: number // 按下死区（字节5 高7位，0-127，0.01mm；有效 0-100）
  releaseRt: number // 释放 RT 灵敏度（字节6-7 低9位，0-511）
  releaseDeadzone: number // 释放死区：0-127（0.01mm；有效 0-100），字节7 高7位
}

/** 测试页平铺行：协议字段 + 键位索引。 */
export type GcomeTravelRow = GcomeKeyTravel & { index: number }

/** GCOME_AP 单键校准值（0xaa / 0xa9，每键 4 字节 IEEE754 单精度浮点）。 */
export interface GcomeCalibrationValue {
  index: number
  /** IEEE754 单精度浮点值（1.81-6.00），未校准键可能为 0。 */
  value: number
}

export type KeyTravel = ClassicKeyTravel | GcomeKeyTravel

/** 键程载荷芯片族（`getKeyTravel` / `setKeyTravel` 判别字段）。 */
export enum KeyTravelFamily {
  /** CH585M / OHID */
  Classic = 'classic',
  /** GCOME_AP */
  Gcome = 'gcome',
}

export interface ClassicKeyTravelInfo {
  family: KeyTravelFamily.Classic // 芯片族：CH585M / OHID
  index: number // 键程索引
  keyTravel: ClassicKeyTravel
}

export interface GcomeKeyTravelInfo {
  family: KeyTravelFamily.Gcome // 芯片族：GCOME_AP
  index: number // 键程索引
  keyTravel: GcomeKeyTravel
}

export type KeyTravelInfo = ClassicKeyTravelInfo | GcomeKeyTravelInfo

export type GcomeKeyTravelWriteItem = {
  family: KeyTravelFamily.Gcome // 芯片族：GCOME_AP
  index: number // 键程索引
  keyTravel: Partial<GcomeKeyTravel> // 补丁：只传要改的字段，其余读-改-写保留
}

export type KeyTravelWriteItem = ClassicKeyTravelInfo | GcomeKeyTravelWriteItem

/** 收窄 `getKeyTravel` 返回的 discriminant union 到 GCOME 条目。 */
export function isGcomeKeyTravelInfo(info: KeyTravelInfo): info is GcomeKeyTravelInfo {
  return info.family === KeyTravelFamily.Gcome
}

/*----------- 单键程及死区格式表 -----------*/

export interface keyboardCalibration {
  ati: boolean // 是否需要要校 ATI,0: 不需要； 1： 需要
  smd: boolean // 是否需要要校 SampleMaxDelta,0: 不需要； 1： 需要
  user: boolean // 是否进入工厂校准； 1：表示是进入用户校准
}

export type protocolCailbrationType = 'ATI' | 'SMD'

export interface deviceCalibration {
  // 行列索引（对应IQS9320 RC_idx 共用体）
  row: number
  col: number
  // row,col 换算后的索引，非原始上报数据
  index: number

  // 状态位（对应IQS9320 StatusBit 位域结构体）
  ATIErr: 0 | 1 // 0: 无错； 1: 有错
  ATIStatus: 0 | 1 | 2 // 0: 无ATI执行； 1: ATI正在执行； 2: ATI执行完成
  MaxDeltaStatus: 0 | 1 | 2 // 0: 无SampleMax执行；1: SampleMax正在执行但还没按下过；2: SampleMax正在执行且有按下过
  ATIHavePress: 0 | 1 // 0: 执行ATI时检测到键正常；1: 执行ATI时检测到键被压下
  KeyDisErr: 0 | 1 // 0: 执行SampleMax时检测键行程正常；1: 执行SampleMax时检测键行程异常

  // 校准上报数据（对应IQS9320 各寄存器值）
  NormalisedDelta: number // 对应IQS9320寄存器0x100E的值
  Movement: number // 对应IQS9320寄存器0x1022的值
  Counts: number // 对应IQS9320寄存器0x105E的值
  LTA: number // 对应IQS9320寄存器0x1086的值
  MulDivPar: number // 对应IQS9320寄存器0x3000的值
  SampleMaxDelta: number // 对应IQS9320寄存器0x3078的值
}
